import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
// import NProgress from 'nprogress';

import { authApi } from '@/utilities/axios';
import { useAuthStore } from '@/modules/auth/store';
import { showToast } from '@/utilities/toast-handler';
import { formatDisplayDate, formatDate } from '@/utilities/date-formatter';
import { getAllIncidentCategories } from '../../incident/constants';
import { getOrderForHttp, getPage, getPerPage } from '@/composables/PaginationComposable';
import { userCompanyPreference } from '@/composables/CompanyPreferenceComposable';

const DEBRIEF_URL = process.env.VUE_APP_API_VERSION + '/debrief';
const SEARCH_EXERCISE_NAME_URL = process.env.VUE_APP_API_VERSION + '/incident-exercises-name';

export const useDebriefStore = defineStore('debrief', () => {
    const authStore = useAuthStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });
    const incident_categories = getAllIncidentCategories();
    const {fetchCompanyPreference} = userCompanyPreference()
    //state
    const debrief = ref({
        incident_id: null,
        appliance_id: null,
        company_id: null,
        learning: false,
        complete: 0,
    });
    const search = ref({
        incidentNo: null,
        learningIdentified: null,
        authorId: null,
        theme_id: null,
        complete: null,
        taskStatus: null,
        created_at_from: null,
        created_at_to: null,
        learning_outcome_complete: null,
        incident_date_from: null,
        incident_date_to: null,
        appliance_id: null,
        category: null,
        debriefId: null,
        exercise_name: null,
    });
    const debriefs = ref([]);
    const loading = ref(false);
    const totalRecords = ref(null);
    const exerciseNameSuggestions = ref([]);

    const params = ref({
        rows: 10,
        sortField: null,
        sortOrder: null,
        page: null,
        sortingInfo: 'created_at desc',
    });
    const editId = ref(null);

    // getters
    const allDebriefs = computed(() => {
        return debriefs.value?.map((debrief) => {
            return {
                ...debrief,
                created_at: formatDisplayDate(debrief.created_at),
                incident_date: formatDate(debrief.incident?.incident_date),
                incident_category: incident_categories.find((incident_category) => incident_category.id == debrief?.incident?.category)?.name,
            };
        });
    });

    //actions
    const createDebrief = async () => {
        try {
            const bodyParams = { ...debrief.value, company_id: company_id.value };
            const response = await authApi.post(DEBRIEF_URL, bodyParams);
            if (response.statusCode === 201) {
                debriefs.value?.push(response.data);
                showToast(response.toast, 'New debrief created successfully');
                return response.data.id;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to create debrief');
        }
    };

    const fetchAllDebriefs = async (list) => {
        try {
            // NProgress.start();
            loading.value = true;
            let page_size = getPerPage(list);
            let page = getPage(list);
            const LINK = DEBRIEF_URL + '?company_id=' + company_id.value + '&page_size=' + page_size + '&page=' + (page + 1);

            const sort = getOrderForHttp(list)
            //console.log(`getOrderForHttp(${list}): `, sort)

            const response = await authApi.get(LINK, {
                params: {
                    ...search.value,
                    theme: search.value.theme_id,
                    learning_identified: search.value.learningIdentified === 'Both' ? null : search.value.learningIdentified,
                    tasksStatus: search.value.taskStatus,
                    author: search.value.authorId,
                    incident_number: search.value.incidentNo,
                    exercise_name: search.value.exercise_name,
                    category: search.value.category,
                    debriefId: search.value.debriefId,
                    sort: sort,
                },
            });
            if (response.statusCode === 200) {
                debriefs.value = response.data.data;
                totalRecords.value = response.data.total;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch debriefs');
        } finally {
            // NProgress.done();
            loading.value = false;
        }
    };

    const searchExerciseNameSuggestions = async () => {
        try {
            exerciseNameSuggestions.value = [];
    
            const response = await authApi.get(SEARCH_EXERCISE_NAME_URL, {
                params: {
                    company_id: company_id.value,
                    page_size: 50,
                    page: 1,
                    sort: 'exercise_name asc',
                    exercise_name: search.value.exercise_name,
                    name_only: true,
                }
            });
    
            if (response.statusCode === 200 && response?.data) {
                exerciseNameSuggestions.value = response.data;
            }
    
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch exercise suggestions');
        }
    };

    const fetchDebriefDetails = async (debriefId) => {
        try {
            const response = await authApi.get(`${DEBRIEF_URL}/${debriefId}`, {
                params: {
                    companyId: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                return response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch details of debrief with Id :' + debriefId);
        }
    };

    const fetchPreference = async () => {
        try {
            return  await fetchCompanyPreference(company_id.value)
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch details of comany preference');
        }
    };

    //helpers
    const initDebrief = () => {
        debrief.value.incident_id = null;
        debrief.value.appliance_id = null;
        debrief.value.company_id = null;
        debrief.value.learning = false;
        debrief.value.complete = 0;
        editId.value = null;
    };

    const initSearch = () => {
        search.value.theme_id = null;
        search.value.learningIdentified = null;
        search.value.complete = null;
        search.value.taskStatus = null;
        search.value.authorId = null;
        search.value.incidentNo = null;
        search.value.learning_outcome_complete = null;
        search.value.created_at_from = null;
        search.value.created_at_to = null;
        search.value.incident_date_from = null
        search.value.incident_date_to = null
        search.value.appliance_id = null
        search.value.category = null
        search.value.debriefId = null
        search.value.exercise_name = null
    };

    const loadEditDebrief = async (debriefData) => {
        debrief.value.appliance_id = debriefData?.appliance_id;
        debrief.value.learning = debriefData?.learning;
    };

    const initparams = () => {
        params.value.rows = 10;
        params.value.sortField = null;
        params.value.sortOrder = null;
        params.value.page = null;
        params.value.sortingInfo = 'created_at desc';
    };

    return {
        //state
        debrief,
        debriefs,
        search,
        loading,
        totalRecords,
        params,
        editId,
        exerciseNameSuggestions,
        // getters
        allDebriefs,
        //actions
        createDebrief,
        fetchAllDebriefs,
        loadEditDebrief,
        fetchDebriefDetails,
        fetchPreference,
        searchExerciseNameSuggestions,
        //helpers
        initDebrief,
        initSearch,
        initparams,
    };
});
